import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	HookStateFunction,
	StoreActionApi,
} from 'react-sweet-state';
import { createStore, createHook, createStateHook, createActionsHook } from 'react-sweet-state';

import { RedactionTourStepSource } from '../redaction-constants.ts';

type InitialState = {
	firstRedactionIdRendered: string | null;
	firstRedactionRenderedForContentId: string | null;
	titleRedactionRenderedForContentId: string | null;
	firstStepSource: RedactionTourStepSource | null;
	activeSpotlightIndex: number | null;
	lastActiveSpotlightIndex: number | null;
};

const initialState: InitialState = {
	firstRedactionIdRendered: null,
	firstRedactionRenderedForContentId: null,
	titleRedactionRenderedForContentId: null,
	firstStepSource: null,
	activeSpotlightIndex: null,
	lastActiveSpotlightIndex: null,
};

const Store = createStore({
	initialState,
	actions: {
		setFirstRedactionIdRendered:
			(
				redactionId: string,
				contentId?: string | null,
			): (({ getState, setState }: StoreActionApi<InitialState>) => void) =>
			({ getState, setState }) => {
				const { firstRedactionIdRendered, firstRedactionRenderedForContentId } = getState();
				if (firstRedactionIdRendered && firstRedactionRenderedForContentId === contentId) return;

				setState({
					firstRedactionIdRendered: redactionId,
					firstRedactionRenderedForContentId: contentId ?? null,
				});
			},
		setTitleRedactionRenderedForContentId:
			(contentId: string | null): (({ setState }: StoreActionApi<InitialState>) => void) =>
			({ setState }) => {
				setState({
					titleRedactionRenderedForContentId: contentId,
				});
			},
		clearTitleRedactionRenderedForContentId:
			(contentId: string): (({ getState, setState }: StoreActionApi<InitialState>) => void) =>
			({ getState, setState }) => {
				if (getState().titleRedactionRenderedForContentId !== contentId) return;

				setState({
					titleRedactionRenderedForContentId: null,
				});
			},
		resetTour:
			(): (({ setState }: StoreActionApi<InitialState>) => void) =>
			({ setState }) => {
				setState({
					activeSpotlightIndex: initialState.activeSpotlightIndex,
					lastActiveSpotlightIndex: initialState.lastActiveSpotlightIndex,
					firstStepSource: initialState.firstStepSource,
				});
			},
		initiateTour:
			(
				firstStepSource: RedactionTourStepSource,
			): (({ getState, setState }: StoreActionApi<InitialState>) => void) =>
			({ getState, setState }) => {
				const {
					activeSpotlightIndex,
					firstRedactionRenderedForContentId,
					titleRedactionRenderedForContentId,
				} = getState();
				if (activeSpotlightIndex !== null) return;
				if (
					firstStepSource === RedactionTourStepSource.bodyRedaction &&
					titleRedactionRenderedForContentId !== null &&
					firstRedactionRenderedForContentId === titleRedactionRenderedForContentId
				) {
					return;
				}

				setState({
					activeSpotlightIndex: 0,
					firstStepSource,
				});
			},
		proceedToNext:
			(): (({ setState, getState }: StoreActionApi<InitialState>) => void) =>
			({ setState, getState }) => {
				const { activeSpotlightIndex } = getState();
				if (activeSpotlightIndex === null) return;

				setState({
					activeSpotlightIndex: activeSpotlightIndex + 1,
					lastActiveSpotlightIndex: activeSpotlightIndex,
				});
			},
		concludeTour:
			(): (({ setState, getState }: StoreActionApi<InitialState>) => void) =>
			({ setState, getState }) => {
				const { activeSpotlightIndex } = getState();

				setState({
					activeSpotlightIndex: null,
					lastActiveSpotlightIndex: activeSpotlightIndex,
					firstStepSource: initialState.firstStepSource,
				});
			},
	},
});

export const useRedactionsTourState: HookFunction<
	InitialState,
	BoundActions<
		InitialState,
		{
			setFirstRedactionIdRendered: (
				redactionId: string,
				contentId?: string | null,
			) => ({ getState, setState }: StoreActionApi<InitialState>) => void;
			setTitleRedactionRenderedForContentId: (
				contentId: string | null,
			) => ({ setState }: StoreActionApi<InitialState>) => void;
			clearTitleRedactionRenderedForContentId: (
				contentId: string,
			) => ({ getState, setState }: StoreActionApi<InitialState>) => void;
			resetTour: () => ({ setState }: StoreActionApi<InitialState>) => void;
			initiateTour: (
				firstStepSource: RedactionTourStepSource,
			) => ({ getState, setState }: StoreActionApi<InitialState>) => void;
			proceedToNext: () => ({ setState, getState }: StoreActionApi<InitialState>) => void;
			concludeTour: () => ({ setState, getState }: StoreActionApi<InitialState>) => void;
		}
	>,
	void
> = createHook(Store);
export const useRedactionsTourActions: HookActionsFunction<
	BoundActions<
		InitialState,
		{
			setFirstRedactionIdRendered: (
				redactionId: string,
				contentId?: string | null,
			) => ({ getState, setState }: StoreActionApi<InitialState>) => void;
			setTitleRedactionRenderedForContentId: (
				contentId: string | null,
			) => ({ setState }: StoreActionApi<InitialState>) => void;
			clearTitleRedactionRenderedForContentId: (
				contentId: string,
			) => ({ getState, setState }: StoreActionApi<InitialState>) => void;
			resetTour: () => ({ setState }: StoreActionApi<InitialState>) => void;
			initiateTour: (
				firstStepSource: RedactionTourStepSource,
			) => ({ getState, setState }: StoreActionApi<InitialState>) => void;
			proceedToNext: () => ({ setState, getState }: StoreActionApi<InitialState>) => void;
			concludeTour: () => ({ setState, getState }: StoreActionApi<InitialState>) => void;
		}
	>
> = createActionsHook(Store);
export const useRedactionsTourIsActive: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: (state) => state.activeSpotlightIndex !== null,
});
